// src/utils/salesPrintData.ts
// Builds print data for a saved sales invoice (same logic as InvoiceDetailModal).
// Used by the direct Print button in the Sales Invoices table.

import type { InvoicePaymentHistoryRow, SalesInvoiceDetail } from '@/services/salesService';
import type { PrintInvoiceData } from './printDocument';

const PM_LABEL: Record<string, string> = {
  CASH: 'Cash',
  CREDIT: 'Credit',
  CREDIT_AMOUNT: 'Customer Credit',
  CREDIT_NOTE: 'Customer Credit',
  PARTIAL: 'Partial Payment',
  UPI: 'UPI',
  CARD: 'Card',
  CHEQUE: 'Cheque',
  NEFT: 'NEFT',
  RTGS: 'RTGS',
  IMPS: 'IMPS',
  BANK_TRANSFER: 'Bank Transfer',
  DD: 'Demand Draft',
  ONLINE: 'Online Payment',
  WALLET: 'Wallet',
  MIXED: 'Mixed',
};

export const getPaymentModeLabel = (mode: string): string => {
  if (!mode) return 'Cash';
  const upper = mode.toUpperCase();
  if (PM_LABEL[upper]) return PM_LABEL[upper];
  return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
};

export function buildSalesPrintData(
  invoice: SalesInvoiceDetail,
  paymentHistory: InvoicePaymentHistoryRow[] = [],
  showPayment = true,
): PrintInvoiceData {
  const inv = invoice as any;

  const paymentStatus: string =
    inv.paymentStatus ??
    (inv.paymentMode === 'CASH' ? 'PAID' : inv.paymentMode === 'PARTIAL' ? 'PARTIAL' : 'UNPAID');

  const breakdown: Record<string, { mode: string; amount: number; status: string }> = {};
  (paymentHistory || [])
    .filter((r) => Number(r.amount || 0) > 0)
    .forEach((r) => {
      const key = (r.paymentMode || 'UNKNOWN').toUpperCase();
      const st = r.paymentStatus || paymentStatus;
      if (!breakdown[key]) breakdown[key] = { mode: key, amount: 0, status: st };
      breakdown[key].amount += Number(r.amount || 0);
      if (breakdown[key].status !== st) breakdown[key].status = 'MIXED';
    });

  return {
    invoiceNo: inv.billNo,
    date: inv.date,
    customerName: inv.partyName,
    customerGstin: inv.customerGstin,
    warehouseName: inv.warehouseName,
    warehouseGstin: inv.warehouseGstin,
    companyName: inv.companyName,
    companyAddress: inv.companyAddress,
    companyGstin: inv.companyGstin,
    companyPhone: inv.companyPhone,
    billingAddress: inv.billingAddress,
    shippingAddress: inv.shippingAddress,
    paymentMode: getPaymentModeLabel(inv.paymentMode || 'CASH'),
    paymentStatus,
    paidAmount: inv.paidAmount ?? 0,
    balanceDue: inv.balanceDue ?? 0,
    isSameState: inv.isSameState,
    subtotal: inv.subtotal,
    totalDiscount: inv.totalDiscount,
    taxableAmount: inv.taxableAmount,
    cgst: inv.totalCGST,
    sgst: inv.totalSGST,
    igst: inv.totalIGST,
    roundOff: inv.roundOff,
    grandTotal: inv.grandTotal,
    showPaymentTable: showPayment,
    paymentModes: Object.values(breakdown).map((p) => ({
      mode: getPaymentModeLabel(p.mode),
      amount: p.amount,
      status: p.status,
    })),
    remainingDue: Number(inv.balanceDue || 0),
    notes: inv.notes,
    items: (inv.items || []).map((item: any, idx: number) => ({
      sr: idx + 1,
      name: item.itemName,
      hsn: item.hsnCode,
      qty: item.qty,
      unit: item.unit,
      rate: item.rate,
      discount: item.discount,
      taxable: item.taxableAmount,
      taxRate: item.taxRate,
      taxAmt: Number(item.cgst || 0) + Number(item.sgst || 0) + Number(item.igst || 0),
      total: item.total,
      cgst: item.cgst,
      sgst: item.sgst,
      igst: item.igst,
    })),
  } as unknown as PrintInvoiceData;
}
